import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Use cookies (httpOnly) AND Bearer token fallback for cross-origin safety
axios.defaults.withCredentials = true;

const AuthContext = createContext({ user: null, loading: true });

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Module-level helper — doesn't need to be re-created per render
export const setToken = (token) => {
  if (token) {
    localStorage.setItem("hv_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem("hv_token");
    delete axios.defaults.headers.common["Authorization"];
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("hv_token");
    if (!stored) {
      // No session on this device — skip the network round-trip (avoids a guaranteed 401 on every public page view)
      setLoading(false);
      return undefined;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${stored}`;
    let cancelled = false;
    axios.get(`${API}/auth/me`)
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) setToken(null);
        setUser(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Logout API failure (network / 401) is non-fatal — we still clear local state.
      // Log so it shows up in monitoring without disturbing the user.
      console.warn("Logout request failed; clearing local session anyway:", err?.message);
    }
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await axios.get(`${API}/auth/me`);
    setUser(data);
    return data;
  }, []);

  // Memoise the context value so consumers don't re-render on every parent render
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser, API }),
    [user, loading, login, register, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
